/**
 * Demo-mode mock data.
 *
 * Activated when VITE_DEMO_MODE === 'true'. Lets the frontend be navigated
 * end-to-end without a running backend (useful for design review, screenshot
 * passes, and demoing the Hub before QB / Mongo / Postgres credentials are
 * provisioned).
 *
 * The shapes here mirror the backend's serialized responses (see
 * `frontend/src/types/api.ts`).
 *
 * The 12 sample conversations below cover the 12 demo questions defined
 * in walker-blue-product-direction-v2.md §"Demo Questions". Numbers are
 * synthesized but consistent with the discovered Walker Blue QB schema
 * (see WB-Vault/Walker-Blue/conventions/services-via-line-items.md).
 */
import type {
  ChartSpec,
  Conversation,
  Message,
  PreferredFormat,
  SearchHit,
  SendMessageResponse,
  User,
} from '../types/api';

let nextMessageId = 100;
function newId(prefix = 'm'): string {
  return `${prefix}_${(nextMessageId++).toString().padStart(6, '0')}`;
}

const MOCK_USER: User = {
  id: 'u_demo',
  email: 'nelson@walker-blue.com',
  name: 'Nelson Mazuki',
  organization: 'walker_blue',
  isAdmin: true,
  // RBAC fields so demo mode exercises the admin console + COO abilities.
  // Owner so the demo shows the full modal (Administration group + Edit Users).
  type: 'human',
  role: 'owner',
  cards: {},
  caps: ['*'],
  cardDepths: { cfo: 'admin', coo: 'admin', cmo: 'admin', ceo: 'admin' },
};

// ---------------------------------------------------------------------------
// Chart specs — the building blocks shared across conversations
// ---------------------------------------------------------------------------

const incomeChart: ChartSpec = {
  chart_type: 'bar',
  title: '179D-tagged revenue, Q4 2025',
  x_axis_label: 'Month',
  y_axis_label: 'Revenue (USD)',
  series: [
    {
      name: '179D revenue',
      data: [
        { x: 'Oct', y: 187_400 },
        { x: 'Nov', y: 214_300 },
        { x: 'Dec', y: 296_120 },
      ],
    },
  ],
  summary_text:
    'Walker Blue earned $697,820 in 179D-tagged revenue during Q4 2025. December was the strongest month, driven by year-end project closeouts.',
};

const marginChart: ChartSpec = {
  chart_type: 'line',
  title: 'Gross margin trend, 2025',
  x_axis_label: 'Month',
  y_axis_label: 'Margin %',
  series: [
    {
      name: 'Gross margin',
      data: [
        { x: 'Jan', y: 0.62 },
        { x: 'Feb', y: 0.61 },
        { x: 'Mar', y: 0.66 },
        { x: 'Apr', y: 0.63 },
        { x: 'May', y: 0.65 },
        { x: 'Jun', y: 0.67 },
        { x: 'Jul', y: 0.7 },
        { x: 'Aug', y: 0.71 },
        { x: 'Sep', y: 0.69 },
        { x: 'Oct', y: 0.72 },
        { x: 'Nov', y: 0.74 },
        { x: 'Dec', y: 0.76 },
      ],
      color: '#1d4ed8',
    },
  ],
  summary_text:
    'Gross margin improved from 62% in Q1 2025 to 76% in December — driven mostly by mix shift toward higher-margin 179D engagements.',
};

const yoy179DChart: ChartSpec = {
  chart_type: 'bar',
  title: '179D revenue, 2024 vs 2025 by quarter',
  x_axis_label: 'Quarter',
  y_axis_label: 'Revenue (USD)',
  series: [
    {
      name: '2024',
      data: [
        { x: 'Q1', y: 412_000 },
        { x: 'Q2', y: 487_500 },
        { x: 'Q3', y: 521_300 },
        { x: 'Q4', y: 596_800 },
      ],
      color: '#94a3b8',
    },
    {
      name: '2025',
      data: [
        { x: 'Q1', y: 538_900 },
        { x: 'Q2', y: 612_400 },
        { x: 'Q3', y: 678_200 },
        { x: 'Q4', y: 697_820 },
      ],
      color: '#1d4ed8',
    },
  ],
  summary_text:
    '179D revenue grew from $2.02M (2024) to $2.53M (2025) — a 25% YoY lift. Every quarter outperformed its prior-year peer; Q3 was the standout (+30%).',
};

const revenueByServiceLineChart: ChartSpec = {
  chart_type: 'pie',
  title: 'Revenue by service line, 2025',
  series: [
    {
      name: '2025 revenue',
      data: [
        { x: 'Epact 179D Certifications', y: 2_527_320 },
        { x: 'Investment Tax Credit (ITC)', y: 1_184_500 },
        { x: '45L Certification / Energy Star', y: 642_800 },
        { x: 'PWA + PWA Finalized', y: 487_200 },
        { x: 'Domestic Content Analysis', y: 312_900 },
        { x: 'Engineering Services + other', y: 218_400 },
      ],
    },
  ],
  summary_text:
    'Total 2025 service revenue: $5.37M. 179D is the largest line at 47%; ITC second at 22%. The 5 IRA-era services (179D / 45L / ITC / PWA / DCA) together are 96% of the book.',
};

const clients179DChart: ChartSpec = {
  chart_type: 'bar',
  title: 'Top 10 clients by 179D revenue, 2025',
  x_axis_label: 'Client',
  y_axis_label: 'Revenue (USD)',
  series: [
    {
      name: '179D revenue',
      data: [
        { x: 'Sterling Property Group', y: 412_800 },
        { x: 'Centurion REIT', y: 321_500 },
        { x: 'Maple Avenue Industrial', y: 298_400 },
        { x: 'Lakeside Logistics Park', y: 276_900 },
        { x: 'Apex Manufacturing', y: 241_300 },
        { x: 'Quentin Tech Campus', y: 219_800 },
        { x: 'Nordic Cold Storage', y: 204_500 },
        { x: 'Coastal Wholesale', y: 187_200 },
        { x: 'Granite State Hospital', y: 172_800 },
        { x: 'Adams County Schools', y: 161_500 },
      ],
      color: '#1d4ed8',
    },
  ],
  summary_text:
    'The top 10 clients represent $2.50M of the $2.53M total 179D revenue (≈99%). Sterling alone is 16%.',
};

const customerTypeChart: ChartSpec = {
  chart_type: 'pie',
  title: 'Revenue by inferred customer type, 2025 (name-pattern bucketed)',
  series: [
    {
      name: '2025 revenue',
      data: [
        { x: 'Building owners / real estate', y: 1_842_300 },
        { x: 'A&E / construction firms', y: 1_315_700 },
        { x: 'ESCOs / energy services', y: 1_098_500 },
        { x: 'Tax-exempt / government', y: 712_400 },
        { x: 'CPA / accounting firms', y: 312_800 },
        { x: 'Other / unknown', y: 90_100 },
      ],
    },
  ],
  summary_text:
    'Building owners + A&E firms together drive 59% of revenue. CAVEAT: customer type is inferred from name patterns (CustomerType field is empty in QB) — see conventions/customer-types.md. A name like "Energy Solutions LLC" could be ESCO or a generic LLC; mapping is approximate.',
};

const monthlyEnergyTrendChart: ChartSpec = {
  chart_type: 'line',
  title: 'Energy tax credit revenue, monthly 2025',
  x_axis_label: 'Month',
  y_axis_label: 'Revenue (USD)',
  series: [
    {
      name: '179D',
      data: [
        { x: 'Jan', y: 142_300 },
        { x: 'Feb', y: 178_400 },
        { x: 'Mar', y: 218_200 },
        { x: 'Apr', y: 198_700 },
        { x: 'May', y: 207_800 },
        { x: 'Jun', y: 205_900 },
        { x: 'Jul', y: 214_100 },
        { x: 'Aug', y: 220_300 },
        { x: 'Sep', y: 243_800 },
        { x: 'Oct', y: 187_400 },
        { x: 'Nov', y: 214_300 },
        { x: 'Dec', y: 296_120 },
      ],
      color: '#1d4ed8',
    },
    {
      name: 'ITC',
      data: [
        { x: 'Jan', y: 38_500 },
        { x: 'Feb', y: 52_100 },
        { x: 'Mar', y: 67_800 },
        { x: 'Apr', y: 82_400 },
        { x: 'May', y: 98_200 },
        { x: 'Jun', y: 110_300 },
        { x: 'Jul', y: 123_500 },
        { x: 'Aug', y: 132_800 },
        { x: 'Sep', y: 142_900 },
        { x: 'Oct', y: 92_400 },
        { x: 'Nov', y: 113_700 },
        { x: 'Dec', y: 129_900 },
      ],
      color: '#10b981',
    },
    {
      name: '45L',
      data: [
        { x: 'Jan', y: 21_400 },
        { x: 'Feb', y: 28_200 },
        { x: 'Mar', y: 41_500 },
        { x: 'Apr', y: 52_300 },
        { x: 'May', y: 47_200 },
        { x: 'Jun', y: 58_400 },
        { x: 'Jul', y: 64_800 },
        { x: 'Aug', y: 72_100 },
        { x: 'Sep', y: 81_500 },
        { x: 'Oct', y: 56_200 },
        { x: 'Nov', y: 64_400 },
        { x: 'Dec', y: 54_800 },
      ],
      color: '#f59e0b',
    },
  ],
  summary_text:
    '179D dominates throughout the year with a strong year-end pop. ITC shows clean growth from $38K (Jan) to $144K (Sep) before a typical Q4 dip. 45L is the smaller line but consistent.',
};

const share179DvsITCChart: ChartSpec = {
  chart_type: 'pie',
  title: '179D vs ITC revenue mix, 2025',
  series: [
    {
      name: '2025 revenue',
      data: [
        { x: 'Epact 179D Certifications', y: 2_527_320 },
        { x: 'Investment Tax Credit (ITC)', y: 1_184_500 },
      ],
    },
  ],
  summary_text:
    'Of the $3.71M combined 179D+ITC revenue, 179D is 68% and ITC is 32%. ITC went from negligible in 2023 to nearly a third of the energy book in 2025 — fastest-growing IRA service.',
};

const growthChart: ChartSpec = {
  chart_type: 'bar',
  title: 'Service line growth: 2025 vs 2024 (revenue %)',
  x_axis_label: 'Service line',
  y_axis_label: 'YoY growth %',
  series: [
    {
      name: 'YoY growth',
      data: [
        { x: 'ITC', y: 4.12 },
        { x: 'Domestic Content Analysis', y: 2.85 },
        { x: 'PWA + PWA Finalized', y: 1.78 },
        { x: '45L', y: 0.42 },
        { x: '179D', y: 0.25 },
        { x: 'Engineering / other', y: -0.08 },
      ],
      color: '#1d4ed8',
    },
  ],
  summary_text:
    'ITC grew 412% YoY (from $231K → $1.18M) — the standout. Domestic Content Analysis (+285%) and PWA (+178%) follow. CAVEAT: ITC, DCA, and PWA all started from a low base in 2024 (post-IRA ramp); growth % from a small denominator can mislead. 179D in absolute dollars added more revenue than any other service.',
};

const concentrationChart: ChartSpec = {
  chart_type: 'pie',
  title: 'Revenue concentration: top 5 clients vs the rest, 2025',
  series: [
    {
      name: '2025 revenue',
      data: [
        { x: 'Top 5 clients', y: 1_550_900 },
        { x: 'Next 25 clients', y: 1_897_400 },
        { x: 'Long tail (300+ clients)', y: 1_924_700 },
      ],
    },
  ],
  summary_text:
    'Top 5 clients = 29% of revenue. The next 25 = 35%. The remaining ~325 active clients = 36%. Concentration is moderate — no single client > 8% of total revenue. Sterling Property Group (8%) is the largest single account.',
};

const vendorSpendChart: ChartSpec = {
  chart_type: 'bar',
  title: 'Top 10 vendors by 2025 spend',
  x_axis_label: 'Vendor',
  y_axis_label: 'Spend (USD)',
  series: [
    {
      name: '2025 spend',
      data: [
        { x: 'Subcontracted Engineering Co.', y: 312_800 },
        { x: 'Aerial Photography Partners', y: 187_400 },
        { x: 'Software & Tools (annual)', y: 142_300 },
        { x: 'Travel — Airlines', y: 98_700 },
        { x: 'Travel — Hotels', y: 76_400 },
        { x: 'Insurance (E&O + Liability)', y: 68_900 },
        { x: 'Office Lease — Boca Raton', y: 54_200 },
        { x: 'Office Lease — Denver', y: 48_700 },
        { x: 'Marketing / Conferences', y: 42_300 },
        { x: 'IT / Hardware', y: 38_900 },
      ],
      color: '#1d4ed8',
    },
  ],
  summary_text:
    'Subcontracted engineering is the largest line at $313K — worth a closer look on margin. Travel combined ($175K) is #2-3 stack; given 4-office distribution that\'s consistent with normal field work. CAVEAT: vendor spend here aggregates Purchases on Amex sub-accounts; QB Bills entity is unused so this is the complete picture.',
};

// ---------------------------------------------------------------------------
// Conversations — one per jefe demo question, in order
// ---------------------------------------------------------------------------

function hoursAgo(h: number): string {
  return new Date(Date.now() - h * 60 * 60 * 1000).toISOString();
}

const c1: Conversation = {
  id: 'c_001',
  personaId: 'cfo',
  title: '179D revenue 2025 vs 2024',
  pinned: true,
  createdAt: hoursAgo(48),
  updatedAt: hoursAgo(0.2),
};
const c2: Conversation = {
  id: 'c_002',
  personaId: 'cfo',
  title: 'Top clients by 179D revenue, 2025',
  pinned: true,
  createdAt: hoursAgo(46),
  updatedAt: hoursAgo(2),
};
const c3: Conversation = {
  id: 'c_003',
  personaId: 'cfo',
  title: 'QB data quality — what’s NOT tracked cleanly',
  pinned: true,
  createdAt: hoursAgo(40),
  updatedAt: hoursAgo(1),
};
const c4: Conversation = {
  id: 'c_004',
  personaId: 'cfo',
  title: 'Revenue by service line, 2025',
  pinned: false,
  createdAt: hoursAgo(38),
  updatedAt: hoursAgo(8),
};
const c5: Conversation = {
  id: 'c_005',
  personaId: 'cfo',
  title: 'Revenue by inferred customer type',
  pinned: false,
  createdAt: hoursAgo(34),
  updatedAt: hoursAgo(10),
};
const c6: Conversation = {
  id: 'c_006',
  personaId: 'cfo',
  title: 'Monthly trend — 179D / ITC / 45L, 2025',
  pinned: false,
  createdAt: hoursAgo(32),
  updatedAt: hoursAgo(12),
};
const c7: Conversation = {
  id: 'c_007',
  personaId: 'cfo',
  title: '179D vs ITC mix',
  pinned: false,
  createdAt: hoursAgo(30),
  updatedAt: hoursAgo(14),
};
const c8: Conversation = {
  id: 'c_008',
  personaId: 'cfo',
  title: 'Fastest-growing services L12M',
  pinned: false,
  createdAt: hoursAgo(28),
  updatedAt: hoursAgo(16),
};
const c9: Conversation = {
  id: 'c_009',
  personaId: 'cfo',
  title: 'Concentration risk — clients & service lines',
  pinned: false,
  createdAt: hoursAgo(26),
  updatedAt: hoursAgo(18),
};
const c10: Conversation = {
  id: 'c_010',
  personaId: 'cfo',
  title: 'Most profitable clients (revenue proxy + caveats)',
  pinned: false,
  createdAt: hoursAgo(24),
  updatedAt: hoursAgo(20),
};
const c11: Conversation = {
  id: 'c_011',
  personaId: 'cfo',
  title: 'Top vendor spend, 2025',
  pinned: false,
  createdAt: hoursAgo(22),
  updatedAt: hoursAgo(21),
};
const c12: Conversation = {
  id: 'c_012',
  personaId: 'cfo',
  title: 'Executive dashboard — service / clients / trends',
  pinned: false,
  createdAt: hoursAgo(20),
  updatedAt: hoursAgo(20),
};

const conversations: Conversation[] = [c1, c2, c3, c4, c5, c6, c7, c8, c9, c10, c11, c12];

// ---------------------------------------------------------------------------
// Messages per conversation
// ---------------------------------------------------------------------------

function userMsg(convId: string, content: string, hoursBack: number, format: PreferredFormat = 'auto'): Message {
  return {
    id: newId('m'),
    conversationId: convId,
    role: 'user',
    content,
    chartSpec: null,
    preferredFormat: format,
    createdAt: hoursAgo(hoursBack),
  };
}

function assistantMsg(
  convId: string,
  content: string,
  hoursBack: number,
  chartSpec: ChartSpec | null = null,
): Message {
  return {
    id: newId('m'),
    conversationId: convId,
    role: 'assistant',
    content,
    chartSpec,
    preferredFormat: null,
    createdAt: hoursAgo(hoursBack),
  };
}

const messages: Record<string, Message[]> = {
  // ---- c_001 — Q1: 179D revenue this year vs prior year ----
  c_001: [
    userMsg(c1.id, 'How much revenue did we generate from 179D work last year, and how does that compare to the prior year?', 48),
    assistantMsg(
      c1.id,
      "Walker Blue's 179D book grew **25% YoY** in 2025 — from **$2.02M** (2024) to **$2.53M** (2025).\n\nQuarter by quarter:\n\n| | 2024 | 2025 | YoY |\n|---|---|---|---|\n| Q1 | $412,000 | $538,900 | +31% |\n| Q2 | $487,500 | $612,400 | +26% |\n| Q3 | $521,300 | $678,200 | +30% |\n| Q4 | $596,800 | $697,820 | +17% |\n\nEvery quarter beat its prior-year peer. Q3 was the standout (+30%), and Q4 — usually the strongest — grew but at a slower pace, suggesting capacity constraints late in the year.\n\n**CAVEAT**: 179D revenue is computed from invoice line items where Item = `Epact 179D Certifications` (Item Id 3). Tagging is reliable — Walker Blue uses Items consistently for service-line attribution.",
      47.99,
      yoy179DChart,
    ),
    userMsg(c1.id, 'And the gross margin trend for 2025?', 0.5, 'chart'),
    assistantMsg(
      c1.id,
      'Gross margin improved steadily through 2025, from **62% in Q1** to **76% in December** — driven mostly by mix shift toward higher-margin 179D engagements. The Q4 lift correlates with the lower-margin 45L closeouts wrapping up.\n\nCAVEAT: COGS allocation uses the standard QB classifier — shared overhead is not allocated by service line. The trend is reliable; the absolute margin figure should be treated as the ceiling.',
      0.2,
      marginChart,
    ),
  ],

  // ---- c_002 — Q3: top clients by 179D revenue ----
  c_002: [
    userMsg(c2.id, 'Which clients generated the most revenue from 179D projects?', 46),
    assistantMsg(
      c2.id,
      "Top 10 clients by 179D revenue in 2025:\n\n| # | Client | 179D Revenue |\n|---|---|---|\n| 1 | Sterling Property Group | $412,800 |\n| 2 | Centurion REIT | $321,500 |\n| 3 | Maple Avenue Industrial | $298,400 |\n| 4 | Lakeside Logistics Park | $276,900 |\n| 5 | Apex Manufacturing | $241,300 |\n| 6 | Quentin Tech Campus | $219,800 |\n| 7 | Nordic Cold Storage | $204,500 |\n| 8 | Coastal Wholesale | $187,200 |\n| 9 | Granite State Hospital | $172,800 |\n| 10 | Adams County Schools | $161,500 |\n\nThese 10 represent **~99% of the $2.53M 179D book** in 2025 — the long tail outside the top 10 is genuinely thin. Sterling alone is **16%** of the 179D line.\n\n**Worth flagging**: 4 of the top 10 are in the same state (according to building records), and 3 of those 4 are tied to the same property-management group. The single-name concentration looks moderate, but the relationship-network concentration may be tighter — worth a closer look in M&A diligence.",
      45.99,
      clients179DChart,
    ),
  ],

  // ---- c_003 — Q12: meta — what QB doesn't track cleanly ----
  c_003: [
    userMsg(c3.id, "What parts of our QuickBooks data aren't tracked cleanly enough to answer executive-level questions?", 40),
    assistantMsg(
      c3.id,
      "Honest catalog, grouped by severity:\n\n## Hard gaps — answers don't exist in QB at all\n\n- **Cost Segregation revenue** — Walker Blue does NOT currently offer this service. Discovered via QB Item catalog: no Item, no Account, no tagging. Not offered.\n- **R&D Tax Credit revenue** — same as Cost Seg. Not offered by Walker Blue.\n- **CRM pipeline / prospects** — QB tracks customers (closed/active relationships), not prospects. Lives in a CRM separately (HubSpot or other — OPEN which one).\n- **Bills-based AP aging** — the `Bill` entity is empty in our QB. Walker Blue pays vendors via Amex, not Bills. So “open invoices we owe vendors” doesn't exist as a queryable concept.\n\n## Soft gaps — answerable but with caveats\n\n- **Customer type segmentation** — the `CustomerType` field is empty. To answer “revenue by accounting-firm clients vs ESCOs” I infer from the customer name (LLP → CPA, Construction → A&E, etc). Mapping is approximate.\n- **Service-line P&L** — all 10 service items roll up to a single Income account. A standard P&L by account collapses the mix into one row. To break out 179D vs ITC vs 45L I walk invoice line items by Item — which works, but means “gross margin by service line” requires COGS allocation we don’t have.\n- **Class-based segmentation** — Class is **disabled** in our QB Preferences. 0% of recent invoices carry ClassRef. Any earlier doc that mentioned Class as a segmentation field is wrong.\n- **TimeActivity (hours) coverage** — quality is OPEN. Engineers may log hours consistently from 2024 onward; senior staff and pre-2024 records are sparse. Until verified, treat hours-based questions as approximate.\n\n## Things QB DOES track that earlier docs got wrong\n\n- **Cardholder identity on Amex** — earlier docs said “card expenses are at the account level only, no cardholder.” That was wrong. The parent account `AMEX 91000` has per-cardholder sub-accounts (e.g., `AMEX 91000:Brian Montenegro 71023`). “Which cardholder spent the most” IS directly answerable.\n\n## Bottom line\n\nThe v1 demo questions all have honest answers, with caveats where appropriate. The biggest gap that affects M&A diligence is **service-line gross margin** — we can answer revenue by line cleanly, but not COGS by line.",
      39.99,
    ),
  ],

  // ---- c_004 — Q2: revenue by service line ----
  c_004: [
    userMsg(c4.id, 'Show me revenue by service line: 179D, 45L, ITC, energy modeling, energy audits.', 38, 'chart'),
    assistantMsg(
      c4.id,
      "Walker Blue's 2025 service revenue mix ($5.37M total):\n\n- **Epact 179D Certifications**: $2.53M (47%)\n- **Investment Tax Credit (ITC)**: $1.18M (22%)\n- **45L Certification / Energy Star**: $643K (12%)\n- **PWA + PWA Finalized**: $487K (9%)\n- **Domestic Content Analysis**: $313K (6%)\n- **Engineering Services + other catch-all**: $218K (4%)\n\n**On 'energy modeling' and 'energy audits'**: those aren't separate Items in our QB catalog. The work that fits those labels is billed under **Engineering Services** (Item 4) or rolled into a 179D engagement (where modeling is required for certification). I can't break out exactly how many hours on the Engineering line were modeling vs audit unless you want me to use the time-tracking system instead — that’s in reactIMS.\n\nThe 5 IRA-era services (179D / 45L / ITC / PWA / DCA) together are **96% of the book**. Walker Blue is essentially an IRA-tax-credit shop with a thin engineering tail.",
      37.99,
      revenueByServiceLineChart,
    ),
  ],

  // ---- c_005 — Q4: customer types ----
  c_005: [
    userMsg(c5.id, 'Which customer types are driving the most revenue: accounting firms, A&E firms, ESCOs, building owners, tax-exempt/government clients?', 34, 'chart'),
    assistantMsg(
      c5.id,
      "**Important caveat first**: QuickBooks' `CustomerType` field is **empty** in our realm — Walker Blue doesn't structurally tag customers by type. The numbers below come from **name-pattern inference** (e.g., “LLP” → CPA, “County” → government). Treat as approximate, not authoritative.\n\nWith that caveat:\n\n- **Building owners / real estate**: $1.84M (34%) — REITs, multifamily partners, commercial owners.\n- **A&E / construction firms**: $1.32M (25%) — engineering firms, electrical contractors, GCs.\n- **ESCOs / energy services**: $1.10M (20%) — Ameresco, Energy Systems Group, Aquila Environmental, etc.\n- **Tax-exempt / government**: $712K (13%) — counties, school districts, public hospitals.\n- **CPA / accounting firms**: $313K (6%) — Anchin Block & Anchin, Armanino, etc.\n- **Other / unknown**: $90K (2%).\n\n**Building owners + A&E firms drive 59% of revenue.** That makes sense for a 179D-heavy book — those are the classic two paths to a 179D claim (owner gets the deduction; designer gets the allocation letter from a tax-exempt project).\n\n**For M&A diligence**: if an acquirer's customer mix is CPA-firm-heavy (Cost Seg + R&D book), the merged entity gets meaningful diversification — both in customer concentration and in cross-sell opportunity.",
      33.99,
      customerTypeChart,
    ),
  ],

  // ---- c_006 — Q5: monthly trends energy ----
  c_006: [
    userMsg(c6.id, 'Show me monthly revenue trends for our energy tax incentive work.', 32, 'chart'),
    assistantMsg(
      c6.id,
      "2025 monthly trend across the 3 main IRA service lines:\n\n- **179D** dominates throughout the year. Strong seasonal uptick in December ($296K) — year-end project closeouts.\n- **ITC** shows clean growth from $38K (Jan) to $144K (Sep), then a typical Q4 dip back to $92K (Oct) before recovering. The shape is consistent with ITC project lifecycle: most engagements close mid-year as renewable projects finalize before tax year cuts.\n- **45L** is the smaller line ($21K-$82K monthly) but consistent — multifamily projects on a more even cadence.\n\nCombined energy book in 2025: **$4.39M** of the $5.37M total ($179D $2.53M + ITC $1.18M + 45L $643K).\n\n**Insight**: ITC is the line that's growing fastest in absolute new dollars added per month (Jan 2024 = ~$8K → Dec 2025 = $130K). If that pace continues, ITC overtakes 45L on annual revenue in 2026.",
      31.99,
      monthlyEnergyTrendChart,
    ),
  ],

  // ---- c_007 — Q6: 179D vs ITC ----
  c_007: [
    userMsg(c7.id, 'What percentage of our revenue is coming from 179D versus newer ITC-related work?', 30, 'chart'),
    assistantMsg(
      c7.id,
      "Of the **$3.71M combined 179D+ITC revenue** in 2025:\n\n- **179D**: $2.53M (68%)\n- **ITC**: $1.18M (32%)\n\nFor context, ITC was negligible in 2023 (essentially $0 — Walker Blue started ITC engagements after the IRA expansion took effect). It's now nearly a third of the energy book — the **fastest-growing IRA service line** by far.\n\n**M&A signal**: this growth pattern says Walker Blue successfully pivoted into the IRA renewable-credit space, opening cross-sell opportunities with R&D and Cost Seg clients whose audience overlaps with Walker Blue's ITC + 179D target (both are tax-driven engineering services).",
      29.99,
      share179DvsITCChart,
    ),
  ],

  // ---- c_008 — Q7: fastest growing ----
  c_008: [
    userMsg(c8.id, 'Which services have grown the fastest over the last 12 months?', 28, 'chart'),
    assistantMsg(
      c8.id,
      "Ranked by YoY growth %:\n\n1. **ITC** — +412% ($231K → $1.18M)\n2. **Domestic Content Analysis** — +285% ($81K → $313K)\n3. **PWA + PWA Finalized** — +178% ($175K → $487K)\n4. **45L** — +42% ($453K → $643K)\n5. **Epact 179D Certifications** — +25% ($2.02M → $2.53M)\n6. **Engineering Services + other** — −8% ($237K → $218K)\n\n**Important caveats**:\n\n- **ITC, DCA, and PWA all started from a low base in 2024** (post-IRA ramp). A growth percentage from a small denominator is misleading — ITC went from $231K to $1.18M, which is huge in % terms but also adds “only” ~$950K to the book.\n- **179D in absolute dollars added more revenue than any other service** ($508K added). Slow % growth, but biggest dollar contributor.\n- **Engineering Services dropped 8%** — likely a mix-shift effect (work that would have been billed as Engineering is now being absorbed into 179D / ITC engagements with bundled scope).\n\n**Insight for diligence**: the right framing is “IRA services are the growth engine; legacy engineering is flat-to-down.” That story holds even after correcting for the small-denominator effect.",
      27.99,
      growthChart,
    ),
  ],

  // ---- c_009 — Q8: concentration risk ----
  c_009: [
    userMsg(c9.id, 'Do we have concentration risk with any major client or service line?', 26, 'chart'),
    assistantMsg(
      c9.id,
      "**Client-side**: moderate concentration. The top 5 clients = **29%** of 2025 revenue. The next 25 = 35%. The remaining ~325 active clients = 36%.\n\nNo single client > 8% of total revenue. **Sterling Property Group at 8% is the largest single account** — but they're concentrated in 179D (Sterling's $413K is all 179D).\n\nThe relationship concentration is tighter than the name concentration suggests: 4 of the top 10 clients map to the same property-management group (per cross-reference with the buildings catalog). If that group consolidates buying decisions, the effective concentration is closer to ~13% on one decision-maker.\n\n**Service-line side**: high concentration. 179D alone is **47% of revenue**. The 5 IRA services together are **96%**. If federal IRA policy changes (extension, phase-down, repeal) disproportionately hit any one credit, the impact is real.\n\n**Mitigations to consider**:\n- Cross-sell ITC and DCA into 179D-only clients to spread risk per relationship.\n- Cross-sell Cost Seg + R&D services if available — they add diversification (not IRA-dependent).\n- Watch the top property-management relationship — cap a single decision-maker's share of bookings.\n\nThis is a story to surface in diligence; it's manageable, not alarming.",
      25.99,
      concentrationChart,
    ),
  ],

  // ---- c_010 — Q9: most profitable ----
  c_010: [
    userMsg(c10.id, 'Which projects or clients appear most profitable, if expenses are tracked well enough to answer that?', 24),
    assistantMsg(
      c10.id,
      "**Honest answer**: I can give you a revenue-only proxy with a clear caveat — true profitability per client requires COGS allocation Walker Blue doesn't currently maintain.\n\n**What QB tracks that I CAN use**:\n- Revenue per client (clean, by line item join)\n- Subcontractor expense per vendor (clean)\n- Direct travel / meals per cardholder (clean — Amex sub-accounts)\n\n**What QB does NOT track that I'd need for true margin**:\n- Hours per client (lives in reactIMS — needs cross-system query, possible once that MCP is wired up)\n- Allocated overhead per project (not done — all overhead lumps into single G&A bucket)\n- Subcontractor cost per project (vendor invoices aren't tagged by project / client)\n\n**Revenue proxy — top 10 clients by 2025 gross revenue**:\n\n| # | Client | Revenue | Best-effort margin estimate |\n|---|---|---|---|\n| 1 | Sterling Property Group | $412,800 | High (large 179D — efficient delivery) |\n| 2 | Centurion REIT | $321,500 | High |\n| 3 | Ameresco, Inc. (ESCO) | $298,200 | Medium (multi-service ITC + DCA) |\n| 4 | Maple Avenue Industrial | $298,400 | High |\n| 5 | Lakeside Logistics Park | $276,900 | High |\n| 6 | Adams County Schools | $232,100 | Medium-High (gov clients are slow-pay but high-margin) |\n| 7 | Apex Manufacturing | $241,300 | High |\n| 8 | Energy Systems Group, LLC | $218,400 | Medium (PWA-heavy, more sub work) |\n| 9 | Quentin Tech Campus | $219,800 | High |\n| 10 | Nordic Cold Storage | $204,500 | High |\n\n**Margin estimates are qualitative judgments**, not from QB. To answer this rigorously, we'd need either:\n1. Cost allocation done in QB (adding ClassRef on expenses tied to revenue Class — but Class is disabled here)\n2. Time tracking from reactIMS joined to revenue from QB (cross-system query — possible, requires reactIMS MCP credentials)\n\nWant me to flag this as an OPEN diligence item?",
      23.99,
    ),
  ],

  // ---- c_011 — Q10: top vendor spend ----
  c_011: [
    userMsg(c11.id, 'Where are we spending the most money by vendor or expense category?', 22, 'chart'),
    assistantMsg(
      c11.id,
      "Top 10 vendors by 2025 spend (aggregating Amex Purchases by VendorRef):\n\n1. **Subcontracted Engineering Co.** — $312,800 (largest single line; worth a margin look)\n2. **Aerial Photography Partners** — $187,400 (drone / site documentation for 179D)\n3. **Software & Tools (annual)** — $142,300 (energy modeling SaaS, CAD, etc.)\n4. **Travel — Airlines** — $98,700\n5. **Travel — Hotels** — $76,400\n6. **Insurance (E&O + Liability)** — $68,900\n7. **Office Lease — Boca Raton** — $54,200\n8. **Office Lease — Denver** — $48,700\n9. **Marketing / Conferences** — $42,300\n10. **IT / Hardware** — $38,900\n\n**Insight**:\n\n- **Subcontracted engineering ($313K)** is your largest external dependency. If those subs are doing 179D delivery, getting visibility into their per-project cost vs. the revenue we book on those projects would directly answer the margin-per-client question (Q9 above).\n- **Travel combined ($175K, lines 4+5)** is consistent with 4-office distributed field work. Per-cardholder breakdown is available if you want to see who's the top spender.\n- **Office leases** at $103K combined across Boca + Denver — Louisville lease isn't in the top 10 (could be lower or in a different category).\n\n**CAVEAT**: this aggregates Purchases on Amex sub-accounts. QB Bills entity is unused so this is the complete vendor-spend picture.",
      21.99,
      vendorSpendChart,
    ),
  ],

  // ---- c_012 — Q11: dashboard combo ----
  c_012: [
    userMsg(c12.id, 'Can you create a dashboard showing: revenue by service line, top clients, monthly trends?', 20, 'chart'),
    assistantMsg(
      c12.id,
      "Building the executive dashboard. Three views below — for the full visual layout open the Dashboard view from the menu (it lays them out side-by-side).\n\n## 1. Revenue by service line, 2025 ($5.37M total)",
      19.95,
      revenueByServiceLineChart,
    ),
    assistantMsg(
      c12.id,
      "## 2. Top 10 clients by 179D revenue, 2025\n\nThese 10 = ~99% of the $2.53M 179D book. Sterling Property Group leads at $413K (16%).",
      19.9,
      clients179DChart,
    ),
    assistantMsg(
      c12.id,
      "## 3. Monthly trend — 179D / ITC / 45L throughout 2025\n\n179D dominates with a Q4 year-end pop. ITC shows the fastest absolute growth — from $38K (Jan) to $144K (Sep). 45L stays smaller but consistent.\n\n**Tip**: click “Dashboard” in the top-right of any conversation to see all chart-bearing messages laid out as a grid for easier visual comparison + a one-click PDF export.",
      19.85,
      monthlyEnergyTrendChart,
    ),
  ],
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Mock router. Returns the parsed shape the real apiRequest would return,
 * or `undefined` if this path is not mocked (caller should fall through).
 */
export async function maybeMock<T>(method: string, path: string, body?: unknown): Promise<T | undefined> {
  // ---------------- AUTH ----------------
  if (path === '/auth/me' && method === 'GET') {
    await sleep(120);
    return { kind: 'human', user: MOCK_USER } as unknown as T;
  }
  if (path === '/auth/login' && method === 'POST') {
    await sleep(280);
    return { user: MOCK_USER } as unknown as T;
  }
  if (path === '/auth/logout' && method === 'POST') {
    await sleep(80);
    return { ok: true } as unknown as T;
  }

  // ---------------- CONVERSATIONS ----------------
  if (path === '/conversations' && method === 'GET') {
    await sleep(160);
    // Return a fresh array (and shallow-cloned items) so React Query's
    // structural-share + tracked notifyOnChangeProps detect the change
    // when a sibling mutation flipped a `pinned` flag in place.
    return { conversations: conversations.map((c) => ({ ...c })) } as unknown as T;
  }
  if (path === '/conversations' && method === 'POST') {
    await sleep(180);
    const id = `c_${Date.now().toString(36)}`;
    const conv: Conversation = {
      id,
      personaId: 'cfo',
      title: 'New conversation',
      pinned: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    conversations.unshift(conv);
    messages[id] = [];
    return { conversation: conv } as unknown as T;
  }
  const convDetailMatch = path.match(/^\/conversations\/([a-z0-9_]+)$/i);
  if (convDetailMatch && method === 'GET') {
    await sleep(140);
    const id = convDetailMatch[1];
    const conv = conversations.find((c) => c.id === id);
    if (!conv) throw new Error(`Mock: unknown conversation ${id}`);
    return { conversation: conv, messages: messages[id] ?? [] } as unknown as T;
  }
  if (convDetailMatch && method === 'PATCH') {
    await sleep(120);
    const id = convDetailMatch[1];
    const conv = conversations.find((c) => c.id === id);
    if (!conv) throw new Error(`Mock: unknown conversation ${id}`);
    const fields = (body ?? {}) as { title?: string; pinned?: boolean };
    if (typeof fields.title === 'string') conv.title = fields.title;
    if (typeof fields.pinned === 'boolean') conv.pinned = fields.pinned;
    conv.updatedAt = new Date().toISOString();
    return { conversation: conv } as unknown as T;
  }
  if (convDetailMatch && method === 'DELETE') {
    await sleep(120);
    const id = convDetailMatch[1];
    const i = conversations.findIndex((c) => c.id === id);
    if (i >= 0) conversations.splice(i, 1);
    delete messages[id];
    return { ok: true } as unknown as T;
  }

  // ---------------- MESSAGES ----------------
  const msgPostMatch = path.match(/^\/conversations\/([a-z0-9_]+)\/messages$/i);
  if (msgPostMatch && method === 'POST') {
    const id = msgPostMatch[1];
    const conv = conversations.find((c) => c.id === id);
    if (!conv) throw new Error(`Mock: unknown conversation ${id}`);
    const b = (body ?? {}) as { content: string; preferred_format: PreferredFormat };
    await sleep(900);
    const userMsgRow: Message = {
      id: newId('m'),
      conversationId: id,
      role: 'user',
      content: b.content,
      chartSpec: null,
      preferredFormat: b.preferred_format,
      createdAt: new Date().toISOString(),
    };
    const wantsChart = b.preferred_format === 'chart' || /chart|trend|breakdown/i.test(b.content);
    const assistantMsgRow: Message = {
      id: newId('m'),
      conversationId: id,
      role: 'assistant',
      content: wantsChart
        ? 'Here is what the numbers look like — I generated a chart for the breakdown you asked about.'
        : "Demo mode: I'm not actually wired to QuickBooks here, but in real usage I'd answer this with live data from your books.",
      chartSpec: wantsChart ? incomeChart : null,
      preferredFormat: null,
      createdAt: new Date().toISOString(),
    };
    if (!messages[id]) messages[id] = [];
    messages[id].push(userMsgRow, assistantMsgRow);
    if (conv.title === 'New conversation') conv.title = b.content.slice(0, 60);
    conv.updatedAt = new Date().toISOString();
    const r: SendMessageResponse = {
      userMessage: userMsgRow,
      assistantMessage: assistantMsgRow,
      cacheHit: false,
      toolsUsed: wantsChart ? ['qb_query', 'generate_chart'] : ['qb_query'],
    };
    return r as unknown as T;
  }

  // ---------------- SEARCH ----------------
  if (path.startsWith('/search?') && method === 'GET') {
    const q = decodeURIComponent(new URLSearchParams(path.split('?')[1]).get('q') ?? '');
    await sleep(180);
    const hits: SearchHit[] = [];
    for (const conv of conversations) {
      for (const m of messages[conv.id] ?? []) {
        if (m.content.toLowerCase().includes(q.toLowerCase())) {
          const idx = m.content.toLowerCase().indexOf(q.toLowerCase());
          const start = Math.max(0, idx - 60);
          const end = Math.min(m.content.length, idx + 60);
          hits.push({
            conversationId: conv.id,
            conversationTitle: conv.title,
            messageId: m.id,
            role: m.role,
            snippet: `…${m.content.slice(start, end)}…`,
            createdAt: m.createdAt,
            score: 1,
          });
        }
      }
    }
    return { query: q, total: hits.length, hits } as unknown as T;
  }

  // ---------------- ADMIN: HEALTH ----------------
  if (path === '/health' && method === 'GET') {
    await sleep(120);
    return {
      status: 'ok',
      version: '0.1.0-demo',
      uptime: Math.floor((Date.now() - DEMO_BOOT_AT) / 1000),
      components: {
        anthropic: { status: 'ok', latencyMs: 142 },
        mongodb: { status: 'ok', latencyMs: 18 },
        mcp_quickbooks: { status: 'ok' },
        mcp_obsidian: { status: 'ok' },
        mcp_inspections: { status: 'ok' },
        mcp_buildings: { status: 'ok' },
        mcp_amex: { status: 'ok' },
        mcp_itc_rfi: { status: 'ok' },
        mcp_d2o: { status: 'ok', details: { mode: 'scaffold' } },
      },
    } as unknown as T;
  }

  // ---------------- ADMIN: SYSTEM (instance resources) ----------------
  if (path === '/admin/system' && method === 'GET') {
    await sleep(90);
    const totalMem = 414 * 1024 * 1024;
    const usedMem = Math.round(totalMem * (0.68 + Math.random() * 0.12)); // ~68–80%
    const totalDisk = 20 * 1024 * 1024 * 1024;
    const usedDisk = Math.round(totalDisk * 0.42);
    const cpuPct = Math.round(8 + Math.random() * 28);
    const load1 = Math.round((0.2 + Math.random() * 0.7) * 100) / 100;
    // Synthesised 30-min history with a memory + CPU spike so the peak markers
    // show a "reached a high level" moment.
    const N = 60;
    const nowMs = Date.now();
    const history = Array.from({ length: N }, (_, i) => {
      const memSpike = i >= 41 && i <= 44;
      const cpuSpike = i === 20 || (i >= 42 && i <= 43);
      return {
        ts: nowMs - (N - 1 - i) * 30_000,
        cpu: cpuSpike ? 68 + Math.round(Math.random() * 22) : Math.round(9 + Math.random() * 18),
        mem: memSpike ? 91 + Math.round(Math.random() * 6) : Math.round(67 + Math.random() * 10),
        disk: 41 + Math.round((i / N) * 3),
      };
    });
    return {
      cpu: { cores: 2, usagePct: cpuPct, loadAvg: [load1, 0.35, 0.3] },
      memory: { totalBytes: totalMem, usedBytes: usedMem, freeBytes: totalMem - usedMem, usedPct: Math.round((usedMem / totalMem) * 100) },
      disk: { totalBytes: totalDisk, usedBytes: usedDisk, freeBytes: totalDisk - usedDisk, usedPct: Math.round((usedDisk / totalDisk) * 100) },
      process: { rssBytes: 49 * 1024 * 1024, uptimeSec: 683_000 },
      host: { platform: 'linux', release: '6.8.0-1018-aws', hostname: 'Walker-Blue', uptimeSec: 690_000 },
      history,
    } as unknown as T;
  }

  // ---------------- ADMIN: RBAC (users / roles / audit — mutable in-session) ----------------
  const rbac = await maybeMockRbac<T>(method, path, body);
  if (rbac !== undefined) return rbac;

  // ---------------- ADMIN: ALERTS ----------------
  if (path === '/admin/alerts' && method === 'GET') {
    await sleep(80);
    // Recipients = users whose effective caps include system.alerts, so toggling
    // that cap on a user in the Users tab makes them appear here.
    const capHit = (granted: string, cap: string): boolean =>
      granted === '*' ||
      (granted === '*.view' && cap.endsWith('.view')) ||
      (granted.endsWith('.*') && (cap === granted.slice(0, -2) || cap.startsWith(`${granted.slice(0, -2)}.`))) ||
      granted === cap;
    const recipients = DEMO_USERS.filter((u) => {
      const eff = u.caps ?? DEMO_ROLES.find((r) => r.id === u.role)?.caps ?? [];
      return eff.some((c) => capHit(c, 'system.alerts'));
    }).map((u) => u.email);
    return { configured: true, enabled: true, smtpConfigured: true, threshold: 98, intervalMin: 60, quietStart: 23, quietEnd: 6, recipients } as unknown as T;
  }
  if (path === '/admin/alerts/test' && method === 'POST') {
    await sleep(420);
    return { sent: true, recipients: [MOCK_USER.email] } as unknown as T;
  }

  // ---------------- ADMIN: USAGE ----------------
  if (path.startsWith('/admin/usage') && method === 'GET') {
    await sleep(160);
    const qp = new URLSearchParams(path.split('?')[1] ?? '');
    const groupBy = qp.get('groupBy') ?? 'total';
    // Scale the baseline 30-day figures by the selected from→to window so the
    // date-range picker visibly changes the numbers in demo mode.
    const fromStr = qp.get('from');
    const toStr = qp.get('to');
    const toMs = toStr ? new Date(toStr).getTime() : Date.now();
    const fromMs = fromStr ? new Date(fromStr).getTime() : toMs - 30 * 86_400_000;
    const days = Math.max(1, Math.round((toMs - fromMs) / 86_400_000));
    const k = days / 30;
    const sc = (n: number) => Math.round(n * k);
    const scUsd = (n: number) => Math.round(n * k * 100) / 100;
    const groups =
      groupBy === 'user'
        ? [
            { ownerKey: 'user:demo-nelson', ownerName: 'Nelson Mazuki', cost: scUsd(4.82), messages: sc(47), tokens: sc(312_400) },
            { ownerKey: 'user:demo-paula', ownerName: 'Paula Velasco (CFO assistant)', cost: scUsd(3.11), messages: sc(34), tokens: sc(198_750) },
            { ownerKey: 'token:demo-zapier', ownerName: 'Zapier integration', cost: scUsd(1.74), messages: sc(21), tokens: sc(122_300) },
            { ownerKey: 'user:demo-greg', ownerName: 'Greg Holt', cost: scUsd(0.96), messages: sc(8), tokens: sc(64_180) },
          ]
        : [{ cost: scUsd(10.63), messages: sc(110), tokens: sc(697_630) }];
    const totalCost = Math.round(groups.reduce((s, g) => s + g.cost, 0) * 100) / 100;
    const totalMessages = groups.reduce((s, g) => s + g.messages, 0);
    const totalTokens = groups.reduce((s, g) => s + g.tokens, 0);
    return {
      from: new Date(fromMs).toISOString(),
      to: new Date(toMs).toISOString(),
      groupBy,
      totalCost,
      totalMessages,
      totalTokens,
      groups,
    } as unknown as T;
  }

  // ---------------- ADMIN: TOP QUESTIONS ----------------
  if (path.startsWith('/admin/top-questions') && method === 'GET') {
    await sleep(140);
    return {
      questions: [
        { normalized: 'what is our 179d revenue this quarter', count: 24, lastAskedAt: new Date(Date.now() - 1000 * 60 * 35).toISOString() },
        { normalized: 'top 10 customers by revenue 2025', count: 22, lastAskedAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString() },
        { normalized: 'gross margin by service line', count: 11, lastAskedAt: new Date(Date.now() - 1000 * 60 * 60 * 9).toISOString() },
        { normalized: 'expenses by class last month', count: 9, lastAskedAt: new Date(Date.now() - 1000 * 60 * 60 * 16).toISOString() },
        { normalized: 'how does the recent merger affect q1', count: 7, lastAskedAt: new Date(Date.now() - 1000 * 60 * 60 * 22).toISOString() },
        { normalized: 'cost segregation studies booked ytd', count: 6, lastAskedAt: new Date(Date.now() - 1000 * 60 * 60 * 30).toISOString() },
        { normalized: 'r&d credit pipeline', count: 5, lastAskedAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString() },
        { normalized: 'aging receivables over 60 days', count: 4, lastAskedAt: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString() },
      ],
    } as unknown as T;
  }

  // ---------------- ADMIN: PURGE CACHE ----------------
  if (path.startsWith('/admin/cache') && method === 'DELETE') {
    await sleep(220);
    return { purged: 23 } as unknown as T;
  }

  // ---------------- ADMIN: COST SERIES (trend chart) ----------------
  if (path.startsWith('/admin/cost-series') && method === 'GET') {
    await sleep(110);
    const days = Number(new URLSearchParams(path.split('?')[1] ?? '').get('days') ?? '7');
    // Deterministic pseudo-random so the curve is realistic (a random walk with
    // weekend dips) yet stable across refetches — no fake repeating sawtooth.
    const seeded = (x: number) => {
      const s = Math.sin(x * 12.9898) * 43_758.5453;
      return s - Math.floor(s);
    };
    const series: { date: string; cost: number }[] = [];
    let level = 0.55;
    for (let k = 0; k < days; k++) {
      const d = new Date();
      d.setDate(d.getDate() - (days - 1 - k));
      const weekend = d.getDay() === 0 || d.getDay() === 6;
      level = Math.max(0.15, Math.min(2.3, level + (seeded(k * 1.73) - 0.46) * 0.4));
      const cost = Math.max(0.05, level * (weekend ? 0.5 : 1) + (seeded(k * 3.31 + 9) - 0.5) * 0.18);
      series.push({ date: d.toISOString().slice(0, 10), cost: Math.round(cost * 100) / 100 });
    }
    return {
      from: series[0]?.date ?? '',
      to: series[series.length - 1]?.date ?? '',
      days: series,
    } as unknown as T;
  }

  // ---------------- ADMIN: ACTIVITY FEED ----------------
  if (path.startsWith('/admin/activity') && method === 'GET') {
    await sleep(120);
    const qp = new URLSearchParams(path.split('?')[1] ?? '');
    const limit = Math.min(500, Math.max(1, Number(qp.get('limit') ?? '50')));
    const fromMs = qp.get('from') ? new Date(qp.get('from')!).getTime() : -Infinity;
    const toMs = qp.get('to') ? new Date(qp.get('to')!).getTime() : Infinity;
    const userQ = (qp.get('user') ?? '').toLowerCase();

    const USERS = ['Nelson Mazuki', 'Paula Velasco', 'Greg Holt', 'Diego Diaz', 'Zapier integration'];
    const TOPICS = ['179D revenue Q4 2025', 'top customers 2025', 'service line growth', 'cardholder spending', 'gross margin by service line', 'concentration risk', 'vendor spend 2025', 'monthly energy trends'];
    const seeded = (x: number) => { const s = Math.sin(x * 12.9898) * 43_758.5453; return s - Math.floor(s); };
    const pick = <U,>(arr: U[], r: number): U => arr[Math.floor(r * arr.length)] ?? arr[0]!;

    interface Row { id: string; ts: string; kind: string; user?: string; text: string; detail?: string }
    const all: Row[] = [];
    for (let i = 0; i < 80; i++) {
      const ts = new Date(Date.now() - Math.round(i * (16 + seeded(i) * 42) * 60_000)).toISOString(); // ~last week
      const u = pick(USERS, seeded(i * 1.7));
      const roll = seeded(i * 3.11);
      let kind = 'message';
      let text = `asked about ${pick(TOPICS, seeded(i * 5.3))}`;
      let detail: string | undefined = `cost $${(0.008 + seeded(i * 2.1) * 0.03).toFixed(3)}`;
      let user: string | undefined = u;
      if (roll >= 0.6 && roll < 0.72) { kind = 'login'; text = 'logged in'; detail = `IP 152.207.48.${20 + Math.floor(seeded(i * 4.4) * 200)}`; }
      else if (roll >= 0.72 && roll < 0.8) { kind = 'error'; text = 'permission denied'; detail = 'missing users.manage'; }
      else if (roll >= 0.8 && roll < 0.88) { kind = 'token'; text = 'used an API token'; detail = 'scopes: ask:write'; }
      else if (roll >= 0.88 && roll < 0.94) { kind = 'cache_purge'; text = 'purged the response cache'; detail = '18 entries cleared'; }
      else if (roll >= 0.94) { kind = 'system'; text = 'QB OAuth token rotated'; detail = 'next refresh in 56 min'; user = undefined; }
      all.push({ id: `ac${i}`, ts, kind, user, text, detail });
    }

    const items = all
      .filter((it) => {
        const t = new Date(it.ts).getTime();
        return t >= fromMs && t <= toMs && (!userQ || (it.user ?? '').toLowerCase().includes(userQ));
      })
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      .slice(0, limit);
    return { items } as unknown as T;
  }

  // ---------------- COO ABILITIES (Tickets + Integrations) ----------------
  const coo = await maybeMockCoo<T>(method, path, body);
  if (coo !== undefined) return coo;

  // Not mocked — let the caller fall through (will fail; that's expected for
  // un-mocked endpoints in demo mode).
  return undefined;
}

// ---------------------------------------------------------------------------
// COO abilities mock — mirrors the gated backend so the Integrations/Tickets
// pages render fully in demo mode. Module-level arrays so toggles/actions
// persist within the session.
// ---------------------------------------------------------------------------
const dIso = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString();
const DEMO_AUTOS = [
  { id: 'a1', name: 'Certified building → HubSpot deal', enabled: true, scope: 'live', createdAt: dIso(20), triggerSystem: 'reactBis', triggerEvent: 'bldg_certified', targetSystem: 'hubspot', targetAction: 'create_deal', fieldMappings: [{ from: 'project', to: 'deal' }, { from: 'sqft', to: 'amount' }] },
  { id: 'a2', name: 'Won opportunity → reactIMS project', enabled: true, scope: 'live', createdAt: dIso(35), triggerSystem: 'insightly', triggerEvent: 'opp_won', targetSystem: 'reactIMS', targetAction: 'create_project', fieldMappings: [{ from: 'opp', to: 'project' }, { from: 'client', to: 'client' }] },
  { id: 'a3', name: 'HubSpot form → Insightly lead', enabled: false, scope: 'test', createdAt: dIso(5), triggerSystem: 'hubspot', triggerEvent: 'form_sub', targetSystem: 'insightly', targetAction: 'create_lead', fieldMappings: [{ from: 'name', to: 'name' }, { from: 'email', to: 'email' }, { from: 'company', to: 'company' }] },
  { id: 'a4', name: 'New HubSpot deal → Insightly opportunity → ReactBis project', enabled: true, scope: 'live', createdAt: dIso(2),
    chain: { trigger: { system: 'hubspot', event: 'deal_created', record: [['Deal name', 'King Kullen 179D'], ['Amount', '$48,000'], ['Company', 'CTI III, LLC']] },
      returns: { id: 'oppId', label: 'Opportunity ID', value: 'OPP-4821', bornAt: 0 },
      hops: [
        { system: 'insightly', action: 'create_opp', maps: [['Deal name', 'Opportunity name'], ['Amount', 'Value']], emits: 'oppId' },
        { system: 'reactBis', action: 'create_project', maps: [['Opportunity name', 'Project name'], ['Company', 'Client']], usesReturn: 'Project reference' },
        { system: 'hubspot', action: 'update_deal', maps: [], usesReturn: 'Opportunity # on the original deal' },
      ] } },
];
const DEMO_EXC = [
  { id: 'e1', autoName: 'HubSpot form → Insightly lead', record: 'Form submission · john@acme.com', at: dIso(1), status: 'open', reason: 'The Company field was empty, so the Insightly lead could not be created.' },
  { id: 'e2', autoName: 'Won opportunity → reactIMS project', record: 'Opportunity · King Kullen 179D', at: dIso(2), status: 'open', reason: 'The client name did not match an existing account in reactIMS.' },
];
let DEMO_APPR = [
  { id: 'p1', title: 'Also copy the building square footage into the HubSpot deal amount', proposedBy: 'Agent', authorKind: 'agent', workflow: 'Certified building → HubSpot deal', drafter: null as string | null, at: dIso(0), diff: [{ field: 'HubSpot deal · Amount', before: '(not mapped)', after: 'ReactBis · Square footage' }] },
];
let demoSeq = 100;
// Provolega projects a ticket can target (mirrors backend PROVOLEGA_PROJECTS).
const DEMO_TICKET_PROJECTS = [
  { slug: 'walkerblueintegrations', name: 'Walker Blue integrations', client: 'Walker Blue' },
  { slug: 'reactbis', name: '(BIS) Building Integration System', client: 'Walker Blue' },
  { slug: 'reactims', name: '(IMS) Inspection Management System', client: 'Walker Blue' },
  { slug: 'itc-qc', name: 'ITC', client: 'Walker Blue' },
  { slug: 'amex-walker-blue', name: 'Amex Walker-Blue', client: 'Walker Blue' },
  { slug: 'walker-blue-hub', name: 'Walker Blue Hubs', client: 'Walker Blue' },
];
interface DemoTicket {
  id: string; requesterId: string | null; requesterName: string;
  projectSlug: string; projectName: string; systemId: string | null; systemLabel: string | null;
  category: string; title: string; description: string; priority: string; dueDate: string | null; status: string;
  comments: { authorName: string; text: string; at: string }[];
  attachments: { fileId: string; filename: string; mime: string; size: number; uploadedByName: string; uploadedAt: string; deliveredToProvolega: boolean }[];
  provolega: null | { taskId: string; url: string | null; assigneeName: string; reviewerName: string | null; pushedById: string | null; pushedByName: string; pushedAt: string };
  liveStatus?: { taskId: string; status: string; reviewState: string | null; label: string; kind: string; since: string | null } | null;
  createdAt: string; updatedAt: string;
}
let DEMO_TICKET_ROWS: DemoTicket[] = [
  {
    id: 't1', requesterId: 'u_demo', requesterName: 'Nelson Mazuki',
    projectSlug: 'reactims', projectName: '(IMS) Inspection Management System', systemId: null, systemLabel: null,
    category: 'bug', title: 'reactIMS calendar sync dropping PTO days',
    description: "PTO pulled from Rippling isn't landing on the reactIMS calendars for some inspectors.",
    priority: 'high', dueDate: null, status: 'sent', comments: [], attachments: [],
    provolega: { taskId: '6a693bc43b68cd7fffb39cd8', url: 'https://provolega.rabbyt.ai/projects/reactims', assigneeName: 'Mazuki Lopez', reviewerName: null, pushedById: 'u_demo', pushedByName: 'Nelson Mazuki', pushedAt: dIso(0) },
    liveStatus: { taskId: '6a693bc43b68cd7fffb39cd8', status: 'in_progress', reviewState: null, label: 'In progress', kind: 'in_progress', since: dIso(0) },
    createdAt: dIso(2), updatedAt: dIso(0),
  },
  {
    id: 't2', requesterId: 'u_diego', requesterName: 'Diego Diaz',
    projectSlug: 'walkerblueintegrations', projectName: 'Walker Blue integrations', systemId: null, systemLabel: null,
    category: 'integration', title: 'Insightly opportunity not syncing to HubSpot',
    description: 'When an Insightly opportunity is won, create/update the matching HubSpot deal automatically.',
    priority: 'urgent', dueDate: null, status: 'submitted', comments: [], attachments: [], provolega: null,
    createdAt: dIso(1), updatedAt: dIso(1),
  },
  // The three outcomes the topbar bell exists for — one each, so the demo shows
  // the notification working instead of an empty bell.
  {
    id: 't3', requesterId: 'u_demo', requesterName: 'Nelson Mazuki',
    projectSlug: 'reactbis', projectName: '(BIS) Building Integration System', systemId: null, systemLabel: null,
    category: 'bug', title: 'Square footage missing on certified buildings export',
    description: 'The CSV export leaves the square-footage column empty for buildings certified this year.',
    priority: 'high', dueDate: null, status: 'sent', comments: [], attachments: [],
    provolega: { taskId: '6a693bc43b68cd7fffb39ce1', url: 'https://provolega.rabbyt.ai/projects/reactbis', assigneeName: 'Mazuki Lopez', reviewerName: null, pushedById: 'u_demo', pushedByName: 'Nelson Mazuki', pushedAt: dIso(3) },
    liveStatus: { taskId: '6a693bc43b68cd7fffb39ce1', status: 'done', reviewState: 'approved', label: 'Done', kind: 'done', since: dIso(1) },
    createdAt: dIso(6), updatedAt: dIso(1),
  },
  {
    id: 't4', requesterId: 'u_demo', requesterName: 'Nelson Mazuki',
    projectSlug: 'itc-qc', projectName: 'ITC', systemId: null, systemLabel: null,
    category: 'integration', title: 'Push ITC QC results into the RFI form',
    description: 'When a QC review closes, write the outcome back onto the originating RFI submission.',
    priority: 'med', dueDate: null, status: 'sent', comments: [], attachments: [],
    provolega: { taskId: '6a693bc43b68cd7fffb39ce2', url: 'https://provolega.rabbyt.ai/projects/itc-qc', assigneeName: 'Leslie Torres', reviewerName: null, pushedById: 'u_demo', pushedByName: 'Nelson Mazuki', pushedAt: dIso(4) },
    liveStatus: { taskId: '6a693bc43b68cd7fffb39ce2', status: 'blocked', reviewState: null, label: 'Blocked', kind: 'blocked', since: dIso(2) },
    createdAt: dIso(8), updatedAt: dIso(2),
  },
  {
    id: 't5', requesterId: 'u_diego', requesterName: 'Diego Diaz',
    projectSlug: 'amex-walker-blue', projectName: 'Amex Walker-Blue', systemId: null, systemLabel: null,
    category: 'feature', title: 'Auto-approve Amex expenses under $50',
    description: 'Skip the approval step for small card expenses to cut the review queue.',
    priority: 'low', dueDate: null, status: 'rejected', comments: [], attachments: [], provolega: null,
    createdAt: dIso(5), updatedAt: dIso(2),
  },
  // Done 9 days ago — past DONE_HIDE_AFTER_DAYS, so it's out of the list (and
  // out of the bell) until someone clicks "Show N completed".
  {
    id: 't6', requesterId: 'u_demo', requesterName: 'Nelson Mazuki',
    projectSlug: 'reactims', projectName: '(IMS) Inspection Management System', systemId: null, systemLabel: null,
    category: 'bug', title: 'Inspector list not sorted by last inspection date',
    description: 'The roster sorts alphabetically; it should lead with whoever has been out longest.',
    priority: 'low', dueDate: null, status: 'sent', comments: [], attachments: [],
    provolega: { taskId: '6a693bc43b68cd7fffb39ce3', url: 'https://provolega.rabbyt.ai/projects/reactims', assigneeName: 'Mazuki Lopez', reviewerName: null, pushedById: 'u_demo', pushedByName: 'Nelson Mazuki', pushedAt: dIso(14) },
    liveStatus: { taskId: '6a693bc43b68cd7fffb39ce3', status: 'done', reviewState: 'approved', label: 'Done', kind: 'done', since: dIso(9) },
    createdAt: dIso(16), updatedAt: dIso(9),
  },
];

const LIVE_CYCLE: { status: string; label: string; kind: string; reviewState: string | null }[] = [
  { status: 'todo', label: 'Queued', kind: 'queued', reviewState: null },
  { status: 'in_progress', label: 'In progress', kind: 'in_progress', reviewState: null },
  { status: 'done', label: 'In review', kind: 'review', reviewState: 'pending' },
  { status: 'done', label: 'Done', kind: 'done', reviewState: 'approved' },
];
function advanceLive(t: DemoTicket): void {
  if (t.status !== 'sent' || !t.provolega) return;
  const cur = t.liveStatus?.kind ?? 'queued';
  const idx = LIVE_CYCLE.findIndex((c) => c.kind === cur);
  const next = LIVE_CYCLE[(idx + 1) % LIVE_CYCLE.length]!;
  t.liveStatus = { taskId: t.provolega.taskId, ...next, since: dIso(0) };
}

async function maybeMockCoo<T>(method: string, path: string, _body?: unknown): Promise<T | undefined> {
  await sleep(120);
  if ((path === '/tickets' || path.startsWith('/tickets?')) && method === 'GET') {
    if (path.includes('refresh')) DEMO_TICKET_ROWS.forEach(advanceLive); // demo: each refresh nudges the status forward
    return { projects: DEMO_TICKET_PROJECTS, provolegaEnabled: true, tickets: DEMO_TICKET_ROWS } as unknown as T;
  }
  if (path === '/tickets' && method === 'POST') {
    const b = _body as { projectSlug: string; title: string; description?: string; priority: string; category?: string; dueDate?: string };
    const proj = DEMO_TICKET_PROJECTS.find((p) => p.slug === b.projectSlug);
    const t: DemoTicket = {
      id: `t${demoSeq++}`, requesterId: MOCK_USER.id, requesterName: MOCK_USER.name,
      projectSlug: b.projectSlug, projectName: proj?.name ?? b.projectSlug, systemId: null, systemLabel: null,
      category: b.category ?? 'integration', title: b.title, description: b.description ?? '',
      // the date picker gives YYYY-MM-DD; the backend stores it at midday UTC
      priority: b.priority, dueDate: b.dueDate ? `${b.dueDate}T12:00:00.000Z` : null,
      status: 'submitted', comments: [], attachments: [], provolega: null,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    DEMO_TICKET_ROWS = [t, ...DEMO_TICKET_ROWS];
    return { ticket: t } as unknown as T;
  }
  const tst = path.match(/^\/tickets\/([^/]+)\/status$/);
  if (tst && method === 'PATCH') {
    const t = DEMO_TICKET_ROWS.find((x) => x.id === tst[1]);
    if (t) { t.status = (_body as { status: string }).status; t.updatedAt = new Date().toISOString(); }
    return { ticket: t } as unknown as T;
  }
  const tcm = path.match(/^\/tickets\/([^/]+)\/comment$/);
  if (tcm && method === 'POST') {
    const t = DEMO_TICKET_ROWS.find((x) => x.id === tcm[1]);
    if (t) { t.comments = [...t.comments, { authorName: MOCK_USER.name, text: (_body as { text: string }).text, at: new Date().toISOString() }]; t.updatedAt = new Date().toISOString(); }
    return { ticket: t } as unknown as T;
  }
  const tatt = path.match(/^\/tickets\/([^/]+)\/attachments$/);
  if (tatt && method === 'POST') {
    await sleep(300);
    const t = DEMO_TICKET_ROWS.find((x) => x.id === tatt[1]);
    const f = (_body ?? {}) as { filename?: string; mime?: string; dataBase64?: string };
    if (t && f.filename) {
      t.attachments = [...(t.attachments ?? []), {
        fileId: `demo${demoSeq++}`,
        filename: f.filename,
        mime: f.mime ?? 'application/octet-stream',
        // Rough decode of the base64 length — the demo never stores the bytes.
        size: Math.round(((f.dataBase64?.length ?? 0) * 3) / 4),
        uploadedByName: MOCK_USER.name,
        uploadedAt: new Date().toISOString(),
        deliveredToProvolega: t.status === 'sent',
      }];
      t.updatedAt = new Date().toISOString();
    }
    return { ticket: t } as unknown as T;
  }
  const tsend = path.match(/^\/tickets\/([^/]+)\/send$/);
  if (tsend && method === 'POST') {
    await sleep(400);
    const t = DEMO_TICKET_ROWS.find((x) => x.id === tsend[1]);
    if (t) {
      // WB user just sends; default assignee = intake owner (reassigns later).
      t.status = 'sent';
      t.provolega = { taskId: `demo${demoSeq++}`, url: `https://provolega.rabbyt.ai/projects/${t.projectSlug}`, assigneeName: 'Mazuki Lopez', reviewerName: null, pushedById: MOCK_USER.id, pushedByName: MOCK_USER.name, pushedAt: new Date().toISOString() };
      t.liveStatus = { taskId: t.provolega.taskId, status: 'todo', reviewState: null, label: 'Queued', kind: 'queued', since: new Date().toISOString() };
      t.updatedAt = new Date().toISOString();
    }
    return { ticket: t } as unknown as T;
  }
  const tdl = path.match(/^\/tickets\/([^/]+)$/);
  if (tdl && method === 'DELETE') {
    DEMO_TICKET_ROWS = DEMO_TICKET_ROWS.filter((x) => x.id !== tdl[1]);
    return {} as unknown as T;
  }
  if (path === '/integrations/workflows' && method === 'GET') return { automations: DEMO_AUTOS } as unknown as T;
  if (path === '/integrations/exceptions' && method === 'GET') return { exceptions: DEMO_EXC.filter((e) => e.status === 'open') } as unknown as T;
  if (path === '/integrations/approvals' && method === 'GET') return { approvals: DEMO_APPR } as unknown as T;
  if (path === '/integrations/dashboard' && method === 'GET') {
    return { automations: DEMO_AUTOS.length, running: DEMO_AUTOS.filter((a) => a.enabled).length, exceptions: DEMO_EXC.filter((e) => e.status === 'open').length, runs24h: 128, health: [] } as unknown as T;
  }
  const tog = path.match(/^\/integrations\/workflows\/([^/]+)\/toggle$/);
  if (tog && method === 'POST') {
    const a = DEMO_AUTOS.find((x) => x.id === tog[1]);
    if (a) a.enabled = !a.enabled;
    return { automation: a } as unknown as T;
  }
  const exc = path.match(/^\/integrations\/exceptions\/([^/]+)\/(retry|skip)$/);
  if (exc && method === 'POST') {
    const e = DEMO_EXC.find((x) => x.id === exc[1]);
    if (e) e.status = 'resolved';
    return { ok: true, how: exc[2] } as unknown as T;
  }
  if (path === '/integrations/agent/draft' && method === 'POST') {
    const text = (_body as { text: string }).text;
    const p = { id: `p${demoSeq++}`, title: text, proposedBy: MOCK_USER.name, authorKind: 'agent', workflow: '(new draft)', drafter: MOCK_USER.id, at: new Date().toISOString(), diff: [{ field: 'New automation', before: '(none)', after: text }] };
    DEMO_APPR = [p, ...DEMO_APPR];
    return { approval: p } as unknown as T;
  }
  const dec = path.match(/^\/integrations\/approvals\/([^/]+)\/decide$/);
  if (dec && method === 'POST') {
    // Demo enforces Separation-of-Duties client-side too: you can't approve your own draft.
    const p = DEMO_APPR.find((x) => x.id === dec[1]);
    if (p && p.drafter === MOCK_USER.id) throw new Error("You drafted this — a different approver must decide (Separation of Duties)");
    DEMO_APPR = DEMO_APPR.filter((x) => x.id !== dec[1]);
    return { ok: true, approved: (_body as { approved: boolean }).approved } as unknown as T;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// RBAC demo store — mutable so editing users/roles PERSISTS within the session
// (create/edit/delete users, edit/clone/delete roles). Toggling caps like
// system.admin / system.alerts on a user actually sticks here.
// ---------------------------------------------------------------------------
interface DemoUser {
  id: string; email: string; name: string; organization: 'walker_blue' | 'rabbyt';
  type: 'human' | 'service'; role: string | null;
  cards: Record<string, string>; caps: string[] | null; isAdmin: boolean;
  createdAt: string; lastLoginAt: string | null;
}
let DEMO_USERS: DemoUser[] = [
  { id: 'u_demo', email: 'nelson@walker-blue.com', name: 'Nelson Mazuki', organization: 'walker_blue', type: 'human', role: 'owner', cards: {}, caps: null, isAdmin: true, createdAt: dIso(120), lastLoginAt: dIso(0) },
  { id: 'u_sam', email: 'sam@walker-blue.com', name: 'Sam Ortega', organization: 'walker_blue', type: 'human', role: 'admin', cards: {}, caps: null, isAdmin: true, createdAt: dIso(75), lastLoginAt: dIso(0) },
  { id: 'u_paula', email: 'paula@walker-blue.com', name: 'Paula Velasco', organization: 'walker_blue', type: 'human', role: 'exec', cards: {}, caps: null, isAdmin: false, createdAt: dIso(90), lastLoginAt: dIso(1) },
  { id: 'u_greg', email: 'greg@walker-blue.com', name: 'Greg Holt', organization: 'walker_blue', type: 'human', role: 'analyst', cards: {}, caps: null, isAdmin: false, createdAt: dIso(60), lastLoginAt: dIso(2) },
  { id: 'u_diego', email: 'diego@walker-blue.com', name: 'Diego Diaz', organization: 'walker_blue', type: 'human', role: 'manager', cards: {}, caps: null, isAdmin: false, createdAt: dIso(45), lastLoginAt: dIso(1) },
];

interface DemoRole { id: string; badge: string; label: string; allCards: string | null; caps: string[]; builtin: boolean }
let DEMO_ROLES: DemoRole[] = [
  { id: 'owner', badge: 'OWNER', label: 'Owner', allCards: 'admin', caps: ['*'], builtin: true },
  { id: 'admin', badge: 'ADMIN', label: 'Platform Admin', allCards: 'admin', caps: ['convo.ask', 'dashboard.*', 'briefing.*', 'tickets.*', 'integrations.*', 'users.manage', 'audit.view', 'system.admin'], builtin: true },
  { id: 'exec', badge: 'EXEC', label: 'Executive', allCards: null, caps: ['convo.ask', 'dashboard.*', 'briefing.*', 'tickets.*', 'integrations.*', 'approvals.decide'], builtin: true },
  { id: 'manager', badge: 'MANAGER', label: 'Manager', allCards: null, caps: ['convo.ask', 'dashboard.view', 'dashboard.pin', 'briefing.view', 'tickets.*', 'integrations.dashboard.view', 'integrations.exceptions.view', 'integrations.exceptions.retry', 'integrations.workflows.view', 'integrations.workflows.toggle_test', 'integrations.workflows.promote_live', 'integrations.agent.draft', 'integrations.approvals.view', 'integrations.approvals.decide'], builtin: true },
  { id: 'analyst', badge: 'ANALYST', label: 'Analyst / Operator', allCards: null, caps: ['convo.ask', 'dashboard.view', 'dashboard.pin', 'briefing.view', 'tickets.view', 'tickets.create', 'tickets.comment', 'tickets.status', 'integrations.dashboard.view', 'integrations.exceptions.view', 'integrations.exceptions.retry', 'integrations.workflows.view', 'integrations.workflows.toggle_test', 'integrations.agent.draft', 'integrations.approvals.view'], builtin: true },
  { id: 'approver', badge: 'APPROVER', label: 'Change Approver', allCards: 'view', caps: ['tickets.view', 'integrations.dashboard.view', 'integrations.exceptions.view', 'integrations.workflows.view', 'integrations.approvals.view', 'integrations.approvals.decide', 'audit.view'], builtin: true },
  { id: 'auditor', badge: 'AUDITOR', label: 'Auditor (read-only)', allCards: 'view', caps: ['*.view', 'briefing.view', 'audit.view'], builtin: true },
  { id: 'guest', badge: 'GUEST', label: 'Guest', allCards: null, caps: ['convo.ask'], builtin: true },
  { id: 'member', badge: 'MEMBER', label: 'Member', allCards: 'manage', caps: ['convo.ask', 'dashboard.*', 'briefing.view'], builtin: true },
  { id: 'service', badge: 'SERVICE', label: 'Service account', allCards: null, caps: [], builtin: true },
];

interface DemoAudit { id: string; at: string; action: string; userId: string | null; apiTokenId: string | null; metadata: Record<string, unknown>; ip: string }
let DEMO_AUDIT: DemoAudit[] = [
  { id: 'au1', at: dIso(0), action: 'user_updated', userId: 'u_demo', apiTokenId: null, metadata: { target: 'paula@walker-blue.com' }, ip: '152.207.48.221' },
  { id: 'au2', at: dIso(1), action: 'role_updated', userId: 'u_demo', apiTokenId: null, metadata: { role: 'analyst' }, ip: '152.207.48.221' },
];
let demoRbacSeq = 100;
const pushAudit = (action: string, metadata: Record<string, unknown>) => {
  DEMO_AUDIT = [{ id: `au${demoRbacSeq++}`, at: new Date().toISOString(), action, userId: 'u_demo', apiTokenId: null, metadata, ip: '127.0.0.1' }, ...DEMO_AUDIT];
};

async function maybeMockRbac<T>(method: string, path: string, body?: unknown): Promise<T | undefined> {
  // ---- Users ----
  if (path === '/admin/users' && method === 'GET') { await sleep(80); return { users: DEMO_USERS, total: DEMO_USERS.length } as unknown as T; }
  if (path === '/admin/users' && method === 'POST') {
    await sleep(150);
    const b = body as { email: string; name: string; role: string; cards?: Record<string, string>; caps?: string[]; type?: 'human' | 'service'; organization?: 'walker_blue' | 'rabbyt' };
    const u: DemoUser = { id: `u_${demoRbacSeq++}`, email: b.email, name: b.name, organization: b.organization ?? 'walker_blue', type: b.type ?? 'human', role: b.role, cards: b.cards ?? {}, caps: b.caps ?? null, isAdmin: b.role === 'admin' || b.role === 'owner', createdAt: new Date().toISOString(), lastLoginAt: null };
    DEMO_USERS = [...DEMO_USERS, u];
    pushAudit('user_created', { email: u.email });
    return { user: u } as unknown as T;
  }
  const um = path.match(/^\/admin\/users\/([^/]+)$/);
  if (um && method === 'PATCH') {
    await sleep(120);
    const u = DEMO_USERS.find((x) => x.id === um[1]);
    if (u) {
      const b = body as Partial<DemoUser>;
      if (b.name !== undefined) u.name = b.name;
      if (b.role !== undefined) { u.role = b.role; u.isAdmin = b.role === 'admin' || b.role === 'owner'; }
      if (b.cards !== undefined) u.cards = b.cards;
      if (b.caps !== undefined) u.caps = b.caps;
      if (b.type !== undefined) u.type = b.type;
    }
    pushAudit('user_updated', { id: um[1] ?? '' });
    return { user: u } as unknown as T;
  }
  if (um && method === 'DELETE') { await sleep(100); DEMO_USERS = DEMO_USERS.filter((x) => x.id !== um[1]); pushAudit('user_deleted', { id: um[1] ?? '' }); return {} as unknown as T; }

  // ---- Roles ----
  if (path === '/admin/roles' && method === 'GET') { await sleep(80); return { roles: DEMO_ROLES } as unknown as T; }
  if (path === '/admin/roles/clone' && method === 'POST') {
    await sleep(150);
    const b = body as { baseId: string; label: string };
    const base = DEMO_ROLES.find((r) => r.id === b.baseId);
    const r: DemoRole = { id: `custom_${demoRbacSeq++}`, badge: 'CUSTOM', label: b.label, allCards: base?.allCards ?? null, caps: [...(base?.caps ?? [])], builtin: false };
    DEMO_ROLES = [...DEMO_ROLES, r];
    pushAudit('role_cloned', { from: b.baseId, label: b.label });
    return { role: r } as unknown as T;
  }
  const rm = path.match(/^\/admin\/roles\/([^/]+)$/);
  if (rm && method === 'PATCH') {
    await sleep(120);
    const r = DEMO_ROLES.find((x) => x.id === rm[1]);
    if (r) r.caps = (body as { caps: string[] }).caps;
    pushAudit('role_updated', { id: rm[1] ?? '' });
    return { role: r } as unknown as T;
  }
  if (rm && method === 'DELETE') { await sleep(100); DEMO_ROLES = DEMO_ROLES.filter((x) => x.id !== rm[1]); pushAudit('role_deleted', { id: rm[1] ?? '' }); return {} as unknown as T; }

  // ---- Audit ----
  if (path.startsWith('/admin/audit') && method === 'GET') { await sleep(90); return { entries: DEMO_AUDIT } as unknown as T; }

  return undefined;
}

const DEMO_BOOT_AT = Date.now();

export const isDemoMode = ((): boolean => {
  try {
    return import.meta.env.VITE_DEMO_MODE === 'true';
  } catch {
    return false;
  }
})();
